import { type Authorization } from '../graphql/queries/getAuthorization';
import { Access } from './accessEnum';

export const validateGeneralAccess = (auth: Authorization, access: Access): boolean => {
  const isValid = (access: Access) => {
    return validateGeneralAccess(auth, access);
  };

  if (
    access === Access.DeviceAlertsConfigurationModal &&
    auth.authorizationAlert?.viewNotificationsSummary
  ) {
    return true;
  }

  if (access === Access.CreateDGAConfigurationModal) {
    if (auth.authorizationDGA?.enableDevices) {
      return true;
    }
  }
  if (access === Access.UpdateDGAConfigurationsModal) {
    return [
      Access.RetryDGASubmissions,
      Access.UpdateDGAGeneralConfiguration,
      Access.DeleteDGAConfiguration,
      Access.UpdateDGASubmissionsConfiguration,
      Access.UpdateDGAMaxFlow,
      Access.SetTicketModal,
      Access.UpdateDGANotificationsConfiguration,
    ].some(isValid);
  }
  if (
    access === Access.UpdateDGAGeneralConfiguration ||
    access === Access.UpdateDGASubmissionsConfiguration ||
    access === Access.DeleteDGAConfiguration
  ) {
    return auth.authorizationDGA.updateInfoDGA;
  }
  if (access === Access.RetryDGASubmissions) return auth.authorizationDGA.retrySending;
  if (access === Access.RetryMassiveDGASubmissions) return auth.authorizationDGA.retryMassiveSending;
  if (access === Access.UpdateDGAMaxFlow) return auth.authorizationDGA.setMaxFlow;
  if (access === Access.UpdateDGANotificationsConfiguration)
    return auth.authorizationDGA.updateNotifications;
  if (access === Access.SetTicketModal) return auth.authorizationDGA.updateTicket;

  if (access === Access.AlertsConfiguration_TableRowAdminView) {
    if (auth.authorizationAlert?.viewNotificationsSummary) {
      return true;
    }
  }
  if (access === Access.AlertsConfiguration_SmsNotifications) {
    if (auth.authorizationAlert?.smsNotifications) {
      return true;
    }
  }
  if (access === Access.UsersTable_OptionsMenu) {
    if (auth.authorizationGeneral?.updateUser || auth.authorizationGeneral?.simulateUser) {
      return true;
    }
  }

  if (
    access === Access.UserOptionsMenu_DeleteUserButton ||
    access === Access.SaveUserModal ||
    access === Access.DeleteUserModal
  ) {
    if (auth.authorizationGeneral?.updateUser) {
      return true;
    }
  }

  if (access === Access.CreateTemporalUserModal) {
    if (auth.authorizationGeneral?.temporaryUser) {
      return true;
    }
  }

  if (access === Access.CreateCollection) {
    // TODO: usar un permiso específico para crear colecciones cuando esté disponible en el backend
    return auth.authorizationGeneral?.updateDeviceCollectionRelation ?? false;
  }

  if (access === Access.DeleteCollection) {
    // TODO: usar un permiso específico para eliminar colecciones cuando esté disponible en el backend
    return auth.authorizationGeneral?.updateDeviceCollectionRelation ?? false;
  }

  if (access === Access.UpdateDeviceCollectionRelationModal) {
    if (auth.authorizationGeneral?.updateDeviceCollectionRelation) {
      return true;
    }
  }

  if (access === Access.UpdateAccountCollectionRelationModal) {
    if (auth.authorizationGeneral?.updateAccountCollectionRelation) {
      return true;
    }
  }

  if (access === Access.UserOptionsMenu_SimulateUserButton || access === Access.SimulateUserModal) {
    if (auth.authorizationGeneral?.simulateUser) {
      return true;
    }
  }

  if (access === Access.OrganizationsFilter) {
    if (auth.authorizationGeneral?.showOrganizationFilter) {
      return true;
    }
  }

  if (access === Access.SaveHardwareConfigurationModal) {
    return auth.authorizationGeneral.updateDeviceHardwareConfiguration;
  }

  if (access === Access.UpdateDeviceAliasOtherOrganizations) {
    return auth.authorizationGeneral.updateDeviceAliasOtherOrganizations;
  }

  if (access === Access.UpdateDeviceUserAccess || access === Access.UpdateUser_DeviceUserAccess) {
    return auth.authorizationGeneral.updateDeviceUserAccess;
  }

  if (access === Access.DeleteDeviceUserAccess) {
    return auth.authorizationGeneral.deleteDeviceUserAccess;
  }

  if (access === Access.CreateDeviceUserAccess) {
    return auth.authorizationGeneral.createDeviceUserAccess;
  }

  if (access === Access.ViewDeviceMetricsPriorityList) {
    return auth.authorizationGeneral.showOrganizationFilter;
  }

  if (access === Access.CreateDevice) {
    return auth.authorizationGeneral.createDevice;
  }

  if (access === Access.ViewDeletedDevices) {
    return auth.authorizationGeneral.createDevice;
  }

  if (access === Access.ViewDeletedUsers) {
    // TODO(P2S-2987): reemplazar createDevice como proxy de ViewDeletedUsers por un permiso
    // específico cuando se refactorice el sistema de autorización del frontend.
    return auth.authorizationGeneral.createDevice;
  }

  if (access === Access.CreateSMAConfiguration) {
    return auth.authorizationSMA.updateInfoSMA;
  }

  if (access === Access.DevicesOperationalStateFilter) {
    return auth.authorizationGeneral.showStatusDeviceFilter;
  }

  if (access === Access.ViewSourceInfo) {
    return auth.authorizationGeneral.viewSourceInfo;
  }

  if (access === Access.DeviceProfileModal) {
    // TODO: create a proper access key
    return auth.authorizationGeneral.createDevice;
  }

  if (access === Access.MetricsViews_ViewConfiguration) {
    return auth.authorizationGeneral.configMetricsView;
  }

  if (access === Access.ShowAuditAdminUsers) {
    return auth.authorizationGeneral.showAuditAdminUsers;
  }

  if (access === Access.UpdateOrganizationPreferences) {
    return auth.authorizationGeneral.updateOrganizationPreferences;
  }

  if (access === Access.UpdateOtherOrganizationPreferences) {
    return auth.authorizationGeneral.updateOtherOrganizationsPreferences;
  }

  if (access === Access.UpdateOtherUsersPreferences) {
    return auth.authorizationGeneral.updateOtherUsersPreferences;
  }

  if (access === Access.ViewActivityLogs) {
    return auth.authorizationGeneral.viewActivityLogs;
  }

  if (access === Access.UpdateMultipleDevicesTagsModal) {
    return auth.authorizationGeneral.updateMultipleDevicesTags;
  }
  if (access === Access.ResendUserCredentials) {
    return auth.authorizationGeneral.resendUserCredentials;
  }

  if (access === Access.HardDeleteUser) {
    return auth.authorizationGeneral.hardDeleteUser;
  }

  if (access === Access.HardDeleteOrganization) {
    return auth.authorizationGeneral.hardDeleteOrganization;
  }

  if (access === Access.GlobalShortcuts) {
    return auth.authorizationGeneral.shortcutsEnabled;
  }

  return false;
};
